import { Box, Chip, Stack, Typography } from "@mui/material";
import { AreaChip } from "components/manualReview/components/AreaChip";
import { MaterialChip } from "components/manualReview/components/MaterialChip";
import { eventTypeColor } from "components/scrapEyeDataDashboard/VideoPlayer/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMaterials } from "hooks/useMaterials";
import { useFormatNumber } from "src/utils/formatNumber";
import { EventType } from "src/store/api/generatedApi";
import { FlaggedEventRow } from "./flaggedEventRows";
import { MassOutlierBandSummary } from "./MassOutlierBandSummary";
import { getTargetedReviewReasonColor } from "./targetedReviewRows";

dayjs.extend(utc);

const EVENT_TIME_FORMAT = "HH:mm:ss";

const EventTypeChip = ({ eventType }: { eventType: EventType }) => (
  <Chip
    size="small"
    label={eventType.toUpperCase()}
    sx={{
      bgcolor: eventTypeColor(eventType),
      color: "common.white",
      fontWeight: "fontWeightBold",
    }}
  />
);

const FlaggedEventHeadline = ({
  row,
  onSelect,
}: {
  row: FlaggedEventRow;
  onSelect: () => void;
}) => {
  const { t } = useTenantTranslation();
  const { materials } = useMaterials();
  const formatNumber = useFormatNumber();
  const formatUnits = useUnitsFormatter(false);
  const { event } = row;

  return (
    <Stack spacing={0.5}>
      <Stack
        direction="row"
        spacing={0.5}
        useFlexGap
        flexWrap="nowrap"
        sx={{ minWidth: 0, overflow: "hidden" }}
      >
        <EventTypeChip eventType={event.eventType} />
        {event.eventType === "flag" ? null : (
          <>
            <AreaChip
              area={event.area ?? ""}
              label={event.areaLabel || t("unknown")}
              selected={false}
              onClick={onSelect}
            />
            <MaterialChip
              material={
                materials?.find(
                  (material) => material.name === event.material
                ) ?? null
              }
              selected={false}
              onClick={onSelect}
            />
            {event.mass == null ? null : (
              <Chip
                size="small"
                variant="outlined"
                label={
                  <Typography variant="body2Mono">
                    {formatNumber(2)(Number(event.mass))}
                    {formatUnits("mass")}
                  </Typography>
                }
              />
            )}
          </>
        )}
        {row.isFlagged ? null : (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label={t("notFlaggedForReview")}
          />
        )}
      </Stack>
      <Typography variant="body2Mono" color="text.secondary">
        {event.start.utc().format(EVENT_TIME_FORMAT)} UTC
      </Typography>
    </Stack>
  );
};

interface TargetedReviewFlaggedEventsProps {
  rows: FlaggedEventRow[];
  onSelectEvent: (eventId: string) => void;
}

export const TargetedReviewFlaggedEvents = ({
  rows,
  onSelectEvent,
}: TargetedReviewFlaggedEventsProps) => {
  const { t } = useTenantTranslation();

  return (
    <Stack spacing={1} sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">
        {rows.some((row) => row.isFlagged) ? t("flaggedEvents") : t("events")} (
        {rows.length})
      </Typography>
      {rows.map((row) => {
        const reasonColor = getTargetedReviewReasonColor(row.event);
        const select = () => {
          onSelectEvent(row.event.id);
        };
        return (
          <Box
            key={row.event.id}
            data-event-id={row.event.id}
            onClick={select}
            sx={{
              p: 1,
              borderRadius: 0.5,
              border: "1px solid",
              borderColor: "grey.300",
              borderLeft: "4px solid",
              borderLeftColor: reasonColor ?? "grey.300",
              cursor: "pointer",
            }}
          >
            <FlaggedEventHeadline row={row} onSelect={select} />
            {row.event.review_reasons.includes("mass_outlier") ? (
              <MassOutlierBandSummary
                band={row.event.mass_outlier_band ?? undefined}
                mass={row.event.mass}
              />
            ) : null}
          </Box>
        );
      })}
    </Stack>
  );
};
